export interface BillingBudget {
  account_id: string;
  business_unit: string | null;
  sub_bu: string | null;
  cloud_type: string | null;
  account_name: string | null;
  bu_head_email: string | null;
  sbu_head_email: string | null;
  owner_email: string | null;
  annual_fixed_budget: number | null;
  annual_budget_revised_month: string | null;
  annual_remaining: number | null;
  /** Client-derived: cumulative net overspend (Σspend − Σbudget) Jan→current month, floored at 0. */
  cumulative_overspend?: number | null;
  saved: number | null;
  ytd_spend: number | null;
  ytd_percentage: number | null;
  percentage_spent_of_total: number | null;
  status: string | null;
  is_flagged: boolean;
  jan_spend: number | null;
  jan_budget: number | null;
  feb_spend: number | null;
  feb_budget: number | null;
  mar_spend: number | null;
  mar_budget: number | null;
  apr_spend: number | null;
  apr_budget: number | null;
  may_spend: number | null;
  may_budget: number | null;
  jun_spend: number | null;
  jun_budget: number | null;
  jul_spend: number | null;
  jul_budget: number | null;
  aug_spend: number | null;
  aug_budget: number | null;
  sep_spend: number | null;
  sep_budget: number | null;
  oct_spend: number | null;
  oct_budget: number | null;
  nov_spend: number | null;
  nov_budget: number | null;
  dec_spend: number | null;
  dec_budget: number | null;
  currency: string | null;
  conversion_rate: number | null;
  updated_at: string | null;
}

export interface BillingBudgetListResponse {
  budgets: BillingBudget[];
  total_count: number;
}

/**
 * Payload for creating a new billing account row. Spend, monthly budgets, revised budget,
 * and derived fields (saved / YTD / % of total / remaining) are not user-settable.
 */
export interface BillingBudgetCreate {
  // Required.
  account_id: string;
  account_name: string;
  cloud_type: string;
  currency: string;
  conversion_rate: number;
  // Optional.
  business_unit?: string | null;
  sub_bu?: string | null;
  bu_head_email?: string | null;
  sbu_head_email?: string | null;
  owner_email?: string | null;
  annual_fixed_budget?: number | null;
  annual_budget_revised_month?: string | null;
  status?: string | null;
  is_flagged?: boolean;
}

export interface GcpBudgetUpsertRequest {
  billing_account_id: string;
  budget_amount: number;
  currency_code: string;
}

export interface GcpBudgetRecord {
  budget_id: string;
  billing_account_id: string;
  display_name: string;
  amount: number;
  currency_code: string;
}

export interface GcpBudgetUpsertResponse {
  action: 'created' | 'updated';
  budget: GcpBudgetRecord;
}

/**
 * The org-wide Business Unit / Sub-BU master list (from /admin/business-units), used to
 * constrain the business_unit and sub_bu fields on billing accounts to a fixed list
 * instead of free text.
 */
export interface BillingBusinessUnitOption {
  id: string;
  name: string;
  sub_business_units: Array<{ id: string; name: string; display_name: string }>;
}

export type BillingBudgetField = keyof BillingBudget;

export interface BillingColumnDef {
  id: BillingBudgetField;
  label: string;
  sticky?: boolean;
  minWidth?: number;
}

export const BILLING_MONTHLY_BUDGET_FIELDS = [
  'jan_budget',
  'feb_budget',
  'mar_budget',
  'apr_budget',
  'may_budget',
  'jun_budget',
  'jul_budget',
  'aug_budget',
  'sep_budget',
  'oct_budget',
  'nov_budget',
  'dec_budget',
] as const satisfies readonly BillingBudgetField[];

export type BillingMonthlyBudgetField = (typeof BILLING_MONTHLY_BUDGET_FIELDS)[number];

export const BILLING_MONTHLY_SPEND_FIELDS: BillingBudgetField[] = [
  'jan_spend',
  'feb_spend',
  'mar_spend',
  'apr_spend',
  'may_spend',
  'jun_spend',
  'jul_spend',
  'aug_spend',
  'sep_spend',
  'oct_spend',
  'nov_spend',
  'dec_spend',
];

export const BILLING_NUMERIC_FIELDS: BillingBudgetField[] = [
  'annual_fixed_budget',
  'annual_remaining',
  'cumulative_overspend',
  'saved',
  'ytd_spend',
  'ytd_percentage',
  'percentage_spent_of_total',
  'jan_spend',
  'jan_budget',
  'feb_spend',
  'feb_budget',
  'mar_spend',
  'mar_budget',
  'apr_spend',
  'apr_budget',
  'may_spend',
  'may_budget',
  'jun_spend',
  'jun_budget',
  'jul_spend',
  'jul_budget',
  'aug_spend',
  'aug_budget',
  'sep_spend',
  'sep_budget',
  'oct_spend',
  'oct_budget',
  'nov_spend',
  'nov_budget',
  'dec_spend',
  'dec_budget',
  'conversion_rate',
];

export const BILLING_EDITABLE_FIELDS: BillingBudgetField[] = [
  'business_unit',
  'sub_bu',
  'cloud_type',
  'account_name',
  'bu_head_email',
  'sbu_head_email',
  'owner_email',
  'annual_fixed_budget',
  'annual_budget_revised_month',
  'annual_remaining',
  'saved',
  'ytd_spend',
  'ytd_percentage',
  'percentage_spent_of_total',
  'status',
  'is_flagged',
  'jan_budget',
  'feb_budget',
  'mar_budget',
  'apr_budget',
  'may_budget',
  'jun_budget',
  'jul_budget',
  'aug_budget',
  'sep_budget',
  'oct_budget',
  'nov_budget',
  'dec_budget',
  'currency',
  'conversion_rate',
];

export const BILLING_TABLE_COLUMNS: BillingColumnDef[] = [
  { id: 'account_id', label: 'Account ID', sticky: true, minWidth: 140 },
  { id: 'account_name', label: 'Account Name', sticky: true, minWidth: 160 },
  { id: 'business_unit', label: 'Business Unit', sticky: true, minWidth: 120 },
  { id: 'sub_bu', label: 'Sub-BU', minWidth: 100 },
  { id: 'cloud_type', label: 'Cloud Type', minWidth: 90 },
  { id: 'bu_head_email', label: 'BU Head Email', minWidth: 180 },
  { id: 'sbu_head_email', label: 'SBU Head Email', minWidth: 180 },
  { id: 'owner_email', label: 'Owner Email', minWidth: 180 },
  { id: 'currency', label: 'Currency', minWidth: 90 },
  { id: 'conversion_rate', label: 'Conversion Rate', minWidth: 110 },
  { id: 'annual_fixed_budget', label: 'Annual Budget ($)', minWidth: 130 },
  { id: 'annual_budget_revised_month', label: 'Budget Revised Month', minWidth: 120 },
  { id: 'annual_remaining', label: 'Annual Remaining ($)', minWidth: 120 },
  { id: 'cumulative_overspend', label: 'Cumulative Overspend ($)', minWidth: 150 },
  { id: 'saved', label: 'Saved ($)', minWidth: 90 },
  { id: 'ytd_spend', label: 'YTD Spend', minWidth: 90 },
  { id: 'ytd_percentage', label: 'YTD %', minWidth: 70 },
  { id: 'percentage_spent_of_total', label: '% Spent of Total', minWidth: 110 },
  { id: 'status', label: 'Status', minWidth: 90 },
  { id: 'is_flagged', label: 'Flagged?', minWidth: 80 },
  { id: 'jan_spend', label: 'Jan Spend', minWidth: 90 },
  { id: 'jan_budget', label: 'Jan Budget', minWidth: 90 },
  { id: 'feb_spend', label: 'Feb Spend', minWidth: 90 },
  { id: 'feb_budget', label: 'Feb Budget', minWidth: 90 },
  { id: 'mar_spend', label: 'Mar Spend', minWidth: 90 },
  { id: 'mar_budget', label: 'Mar Budget', minWidth: 90 },
  { id: 'apr_spend', label: 'Apr Spend', minWidth: 90 },
  { id: 'apr_budget', label: 'Apr Budget', minWidth: 90 },
  { id: 'may_spend', label: 'May Spend', minWidth: 90 },
  { id: 'may_budget', label: 'May Budget', minWidth: 90 },
  { id: 'jun_spend', label: 'Jun Spend', minWidth: 90 },
  { id: 'jun_budget', label: 'Jun Budget', minWidth: 90 },
  { id: 'jul_spend', label: 'Jul Spend', minWidth: 90 },
  { id: 'jul_budget', label: 'Jul Budget', minWidth: 90 },
  { id: 'aug_spend', label: 'Aug Spend', minWidth: 90 },
  { id: 'aug_budget', label: 'Aug Budget', minWidth: 90 },
  { id: 'sep_spend', label: 'Sep Spend', minWidth: 90 },
  { id: 'sep_budget', label: 'Sep Budget', minWidth: 90 },
  { id: 'oct_spend', label: 'Oct Spend', minWidth: 90 },
  { id: 'oct_budget', label: 'Oct Budget', minWidth: 90 },
  { id: 'nov_spend', label: 'Nov Spend', minWidth: 90 },
  { id: 'nov_budget', label: 'Nov Budget', minWidth: 90 },
  { id: 'dec_spend', label: 'Dec Spend', minWidth: 90 },
  { id: 'dec_budget', label: 'Dec Budget', minWidth: 90 },
];
